import {startCase} from "lodash";
import {
  PAYMENT_METHOD_DISPLAY_FLOW_TYPE,
  PaymentFlowFeatureFlag,
  PaymentFlowUpdatePayload,
  PaymentMethodDisplayFlow,
  PaymentMethodDisplayPolicy,
} from "../../../../apis/requests/payment-flows/types";
import {ParamSchema} from "../../../../apis/requests/segments/types";
import {
  buildConditionsPayload,
  flowToBaseForm,
  getDefaultControlPolicy,
  getProductionPolicy,
} from "../../flow-form/helpers";
import {CURRENT_CHECKOUT_POLICY} from "../constants";
import type {FormValues, VariantFormValues} from "./schema";

const copyPolicy = (policy: PaymentMethodDisplayPolicy): PaymentMethodDisplayPolicy => ({
  methods: [...policy.methods],
  default: policy.default,
});

export const getDefaultControlDisplayPolicy = (
  defaultFlow: PaymentMethodDisplayFlow | undefined
): PaymentMethodDisplayPolicy => copyPolicy(getDefaultControlPolicy(defaultFlow) ?? CURRENT_CHECKOUT_POLICY);

export const buildVariantsFromFlag = (
  flag: PaymentFlowFeatureFlag,
  defaultFlow: PaymentMethodDisplayFlow | undefined
): VariantFormValues[] =>
  Object.entries(flag.variant_weights).map(([name, weight]) => ({
    name,
    weight,
    label: defaultFlow?.config.variants[name]?.label ?? startCase(name),
    policy: copyPolicy(getProductionPolicy(defaultFlow, name) ?? CURRENT_CHECKOUT_POLICY),
  }));

export const buildPaymentFlowPayload = (
  values: FormValues,
  parameters: ParamSchema[]
): PaymentFlowUpdatePayload<typeof PAYMENT_METHOD_DISPLAY_FLOW_TYPE> => ({
  name: values.name.trim(),
  type: PAYMENT_METHOD_DISPLAY_FLOW_TYPE,
  conditions: buildConditionsPayload(values, parameters),
  config: {
    feature_flag: values.feature_flag,
    variants: Object.fromEntries(
      values.variants.map((variant) => [
        variant.name,
        {weight: variant.weight, label: variant.label, policy: copyPolicy(variant.policy)},
      ])
    ),
  },
});

export const flowToForm = (flow: PaymentMethodDisplayFlow): FormValues => ({
  ...flowToBaseForm(flow),
  variants: Object.entries(flow.config.variants).map(([name, variant]) => ({
    name,
    weight: variant.weight,
    label: variant.label,
    policy: copyPolicy(variant.policy),
  })),
});
